import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, Loader2 } from 'lucide-react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { definitionErrorLines, parseDefinitionJson } from '@/lib/template-admin';
import { formTemplatesService } from '@/services/formTemplates';
import toastService from '@/services/toast/service';

interface ImportDefinitionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ImportDefinitionDialog({ open, onOpenChange }: ImportDefinitionDialogProps) {
  const navigate = useNavigate();
  const fileInput = useRef<HTMLInputElement>(null);
  const [text, setText] = useState('');
  const [parseError, setParseError] = useState<string | null>(null);
  const [errorLines, setErrorLines] = useState<string[]>([]);
  const [importing, setImporting] = useState(false);

  const clearErrors = () => {
    setParseError(null);
    setErrorLines([]);
  };

  useEffect(() => {
    if (open) {
      setText('');
      clearErrors();
    }
  }, [open]);

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Reset so choosing the same file again fires another change event.
    e.target.value = '';
    if (!file) return;
    try {
      setText(await file.text());
      clearErrors();
    } catch {
      setParseError("The file couldn't be read");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (importing || text.trim() === '') return;
    clearErrors();
    const parsed = parseDefinitionJson(text);
    if (!parsed.ok) {
      setParseError(parsed.error);
      return;
    }
    setImporting(true);
    try {
      const { publicId } = await formTemplatesService.importDefinition(parsed.definition);
      toastService.success('Imported as a draft');
      onOpenChange(false);
      navigate(`/templates/${publicId}`);
    } catch (err) {
      setErrorLines(definitionErrorLines(err));
    } finally {
      setImporting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !importing && onOpenChange(o)}>
      <DialogContent className="sm:max-w-2xl">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Import definition</DialogTitle>
            <DialogDescription>
              Paste a template definition (the JSON Export JSON saves) or choose a file. It becomes a draft template
              with its own studio form.
            </DialogDescription>
          </DialogHeader>
          <DialogBody className="space-y-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="import-definition-json">Definition</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => fileInput.current?.click()}
                  disabled={importing}
                >
                  Choose file
                </Button>
                <input
                  ref={fileInput}
                  type="file"
                  accept=".json,application/json"
                  className="hidden"
                  onChange={handleFile}
                  disabled={importing}
                />
              </div>
              <Textarea
                id="import-definition-json"
                rows={10}
                className="font-mono text-xs"
                value={text}
                onChange={(e) => {
                  setText(e.target.value);
                  clearErrors();
                }}
                placeholder="Paste the JSON from Export JSON, or choose a file"
                spellCheck={false}
                disabled={importing}
                aria-invalid={parseError !== null}
                aria-describedby={parseError ? 'import-definition-error' : undefined}
              />
              {parseError && (
                <p id="import-definition-error" className="text-xs text-destructive">
                  {parseError}
                </p>
              )}
            </div>
            {errorLines.length > 0 && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  {errorLines.map((line, i) => (
                    <p key={i}>{line}</p>
                  ))}
                </AlertDescription>
              </Alert>
            )}
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={importing}>
              Cancel
            </Button>
            <Button type="submit" disabled={importing || text.trim() === ''}>
              {importing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Importing...
                </>
              ) : (
                'Import'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
